import React from "react";
import ContentShell from "components/ContentShell";
import { SimpleTextInput } from "components/FormTextInput";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import WorldCupModeConfirmDialog from "./WorldCupModeConfirmDialog";
import { SimpleMultiSelectInput } from "components/FormMultiSelectInput";
import sessionHelperInstance from "services/sessionHelper";
import { SimpleSelectInput } from "components/FormSelectInput";
import { useSnackbar } from "hooks/useSnackbar";
import { makeStyles } from "@mui/styles";
import {
  InfoOutlined,
  Save,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  createAndUpdateConfig,
  getConfigurations,
  toggleWorldCupMode,
} from "services/miscService";
import SimpleSwitch from "components/Switch";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";

const styles = makeStyles((theme) => ({
  field: {
    display: "flex",
    gap: 10,
    width: 220,
  },
  saveButton: {
    marginTop: 8,
  },
  buttonStyle: {
    padding: 0,
    marginTop: 20,
  },
  configurationCard: {
    background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
    border: "1px solid #e9ecef",
    borderRadius: "12px",
    padding: "20px",
    minWidth: "300px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
  },
  objectConfigCard: {
    background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
    border: "1px solid #e9ecef",
    borderRadius: "12px",
    padding: "24px",
    minWidth: "350px",
    maxWidth: "450px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
  },
  // Wider variant for object-configs with many/paired fields (e.g. Team Name
  // Configuration) - spans exactly 2 grid tracks (roughly 2 normal cards'
  // width) instead of sharing a row with the much smaller cards, and drops
  // the 450px cap so paired columns have room to breathe.
  objectConfigCardWide: {
    background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
    border: "1px solid #e9ecef",
    borderRadius: "12px",
    padding: "24px",
    minWidth: "760px",
    gridColumn: "span 2",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
  },
  configHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "2px solid #1D1030",
  },
  configTitle: {
    fontWeight: "600 !important",
    fontSize: "16px !important",
    color: "#1D1030",
    margin: "0 !important",
  },
  saveIconButton: {
    backgroundColor: "#1D1030",
    color: "white",
    padding: "8px",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#3F3350",
      transform: "scale(1.05)",
    },
  },
  fieldContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "100%",
  },
  simpleConfigContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    width: "100%",
  },
  inputFieldWrapper: {
    flex: 1,
    minWidth: "200px",
  },
  sectionTitle: {
    fontSize: "20px !important",
    fontWeight: "700 !important",
    color: "#1D1030",
    marginBottom: "24px !important",
    paddingLeft: "4px",
    borderLeft: "4px solid #1D1030",
    paddingBottom: "8px",
  },
  configGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "24px",
    marginBottom: "32px",
  },
  objectConfigGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
    gap: "24px",
    alignItems: "start",
  },
  pairedFieldsContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  fieldFullWidth: {
    gridColumn: "1 / -1",
  },
  objectFieldsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
}));

const configurationsList = [
  {
    name: "SHOW_INITIAL_PAYTABLE",
    label: "Initial Paytable",
    type: "multiSelect", // type can be - "multiSelect" | "select" | "textInput" | "switch"
    op: "countryOptions", // can be null if not a select input
    value: "SHOW_INITIAL_PAYTABLE",
  },
  {
    name: "OPENING_CLOSING_BALANCE",
    label: "Open/Close Balance Territories",
    type: "multiSelect",
    op: "countryOptions",
    value: "OPENING_CLOSING_BALANCE",
  },
  {
    name: "MAX_BOOSTER_APPLY_LIMIT",
    label: "Maximum Booster Limit",
    type: "textInput",
    op: null,
    value: "MAX_BOOSTER_APPLY_LIMIT",
    dataType: "Number",
  },
  {
    name: "FTUE_MATCH_OVR_DIFF",
    label: "FTUE Match OVR Diff",
    type: "textInput",
    op: null,
    value: "FTUE_MATCH_OVR_DIFF",
    dataType: "Number",
  },
  {
    name: "DEFAULT_XP_PER_STAKE_UNIT",
    label: "XP Per Stake",
    type: "textInput",
    op: null,
    value: "DEFAULT_XP_PER_STAKE_UNIT",
    dataType: "Number",
  },
  {
    name: "USE_GOOGLE_ANALYTICS",
    label: "Enable Google Analytics?",
    type: "switch",
    op: null,
    value: "USE_GOOGLE_ANALYTICS",
  },
  {
    name: "HIDE_RESET_BTN",
    label: "Hide Reset Game Button?",
    type: "switch",
    op: null,
    value: "HIDE_RESET_BTN",
  },
  {
    name: "MM_GHOST_TEAM_DAYS",
    label: "Matchmaking Ghost Team Days",
    type: "textInput",
    op: null,
    value: "MM_GHOST_TEAM_DAYS",
    dataType: "Number",
    requiredPermission: PermittedRoles.CONFIGURATIONS.UPDATE_ROLES,
  },
  {
    name: "VAULT_CONFIGURATION",
    label: "Vault Configuration",
    type: "object",
    value: "VAULT_CONFIGURATION",
    objectValues: [
      {
        name: "baseXP",
        label: "Base XP",
        type: "textInput",
        op: null,
        value: "baseXP",
        dataType: "Number",
      },
      {
        name: "growthRate",
        label: "Growth Rate",
        type: "textInput",
        op: null,
        value: "growthRate",
        dataType: "Number",
      },
      {
        name: "roundOffToNearest",
        label: "Round off to Nearest",
        type: "textInput",
        op: null,
        value: "roundOffToNearest",
        dataType: "Number",
      },
      {
        name: "maxVaultLevel",
        label: "Max Vault Level",
        type: "textInput",
        op: null,
        value: "maxVaultLevel",
        dataType: "Number",
      },
      {
        name: "maxSpinCount",
        label: "Max Vault Spins",
        type: "textInput",
        op: null,
        value: "maxSpinCount",
        dataType: "Number",
      },
    ],
  },
  {
    name: "CHEAT_CONFIG",
    label: "Cheat Config",
    type: "object",
    value: "CHEAT_CONFIG",
    objectValues: [
      {
        name: "useCheat",
        label: "Allow Debug Build?",
        type: "switch",
        op: null,
        value: "useCheat",
      },
    ],
  },
  {
    name: "VOID_CHECK_DELAY_TIME",
    label: "Void Check Delay Config",
    type: "object",
    value: "VOID_CHECK_DELAY_TIME",
    objectValues: [
      {
        name: "value",
        label: "Time",
        type: "textInput",
        op: null,
        value: "value",
        dataType: "Number",
      },
      {
        name: "unit",
        label: "Unit",
        type: "select",
        op: "unitOptions",
        value: "unit",
      },
    ],
  },
  {
    name: "SEASON_POINT_CONFIG",
    label: "End of Season Points Per GBP",
    type: "object",
    value: "SEASON_POINT_CONFIG",
    objectValues: [
      {
        name: "pointForBet",
        label: "For Bet",
        type: "textInput",
        op: null,
        value: "pointForBet",
        dataType: "Number",
      },
      {
        name: "pointForIAP",
        label: "For Coin Purchase",
        type: "textInput",
        op: null,
        value: "pointForIAP",
        dataType: "Number",
      },
      {
        name: "pointForLeague",
        label: "For League Clearing",
        type: "textInput",
        op: null,
        value: "pointForLeague",
        dataType: "Number",
      },
    ],
  },
  {
    name: "QUICK_SWAP_CONFIG",
    label: "Quick Swap Configuration",
    type: "object",
    value: "QUICK_SWAP_CONFIG",
    objectValues: [
      {
        name: "ovrThreshold",
        label: "OVR Threshold",
        type: "textInput",
        op: null,
        value: "ovrThreshold",
        dataType: "Number",
      },
      {
        name: "matchFrequency",
        label: "Match Frequency",
        type: "textInput",
        op: null,
        value: "matchFrequency",
        dataType: "Number",
      },
    ],
  },
  // defaultValue/description on each field below mirrors pbf_game-be's
  // DEFAULT_TEAM_NAME_CONFIG (libs/shared/constants/TeamNameConfig.ts) and
  // DEFAULT_SIGHTENGINE_MODERATION_CONFIG (libs/modules/profanity/sightengine.service.ts)
  // - keep in sync if those change. Field order below is deliberate (paired,
  // 2-per-row) - see the "paired" flag and renderObjectField's grid layout.
  {
    name: "TEAM_NAME_CONFIG",
    label: "Team Name Configuration",
    type: "object",
    value: "TEAM_NAME_CONFIG",
    paired: true,
    objectValues: [
      {
        name: "editEnabled",
        label: "Edit Enabled",
        type: "switch",
        op: null,
        value: "editEnabled",
        defaultValue: false,
        description: "Master toggle for team name editing. When off, no rename attempts are allowed at all.",
      },
      {
        name: "localProfanityEnabled",
        label: "Local Profanity Enabled",
        type: "switch",
        op: null,
        value: "localProfanityEnabled",
        defaultValue: false,
        description: "Enables an additional local profanity wordlist check, on top of Sightengine.",
      },
      {
        name: "minLength",
        label: "Min Length",
        type: "textInput",
        op: null,
        value: "minLength",
        dataType: "Number",
        defaultValue: 3,
        description: "Minimum allowed team name length, in characters.",
      },
      {
        name: "maxLength",
        label: "Max Length",
        type: "textInput",
        op: null,
        value: "maxLength",
        dataType: "Number",
        defaultValue: 12,
        description: "Maximum allowed team name length, in characters.",
      },
      {
        name: "consecutiveNumbersCheckEnabled",
        label: "Consecutive Numbers Check Enabled",
        type: "switch",
        op: null,
        value: "consecutiveNumbersCheckEnabled",
        defaultValue: false,
        description:
          "When on, team names with too many digits in a row are rejected (e.g. to block phone numbers).",
      },
      {
        name: "consecutiveNumbersLimit",
        label: "Consecutive Numbers Limit",
        type: "textInput",
        op: null,
        value: "consecutiveNumbersLimit",
        dataType: "Number",
        defaultValue: 3,
        description:
          "Maximum digits allowed in a row. Minimum 1. 3 means 'ABC123' is allowed and 'ABC1234' is blocked.",
      },
      {
        name: "attemptsLimit",
        label: "Abuse-Lock Attempts Limit",
        type: "textInput",
        op: null,
        value: "attemptsLimit",
        dataType: "Number",
        defaultValue: 5,
        description: "Number of rejected (profanity/format) attempts allowed before the abuse lock kicks in.",
      },
      {
        name: "offensiveLockDays",
        label: "Offensive Lock Days",
        type: "textInput",
        op: null,
        value: "offensiveLockDays",
        dataType: "Number",
        defaultValue: 7,
        description: "How many days a user is locked out of renaming after hitting the abuse-lock attempts limit.",
      },
      {
        name: "maxChangesPerPeriod",
        label: "Max Changes Per Period",
        type: "textInput",
        op: null,
        value: "maxChangesPerPeriod",
        dataType: "Number",
        defaultValue: 1,
        description: "Successful renames allowed within one rolling period, for players past FTU. 0 = unlimited.",
      },
      {
        name: "periodDays",
        label: "Period Days",
        type: "textInput",
        op: null,
        value: "periodDays",
        dataType: "Number",
        defaultValue: 7,
        description: "Length of the rolling rename-quota window, in days, for players past FTU.",
      },
      {
        name: "ftuMaxAttempts",
        label: "FTU Max Attempts",
        type: "textInput",
        op: null,
        value: "ftuMaxAttempts",
        dataType: "Number",
        defaultValue: 3,
        description: "Total rename attempts (successful or not) allowed during first-time onboarding, before the auto-generated name is kept.",
      },
      {
        name: "ftuLockDays",
        label: "FTU Lock Days",
        type: "textInput",
        op: null,
        value: "ftuLockDays",
        dataType: "Number",
        defaultValue: 0,
        description: "Days to lock renaming after exhausting FTU attempts. 0 = no lock, user just keeps their current name.",
      },
      {
        name: "throttleEnabled",
        label: "Throttle Enabled",
        type: "switch",
        op: null,
        value: "throttleEnabled",
        defaultValue: true,
        description: "Rate-limits how often a user can check name availability while typing.",
      },
      {
        name: "throttleMs",
        label: "Throttle Ms",
        type: "textInput",
        op: null,
        value: "throttleMs",
        dataType: "Number",
        defaultValue: 500,
        description: "Minimum milliseconds between live name-availability checks, when throttling is enabled.",
      },
      {
        name: "sightengine",
        label: "Sightengine (Content Moderation)",
        type: "group",
        value: "sightengine",
        fullWidth: true,
        objectValues: [
          {
            name: "enabled",
            label: "Enabled",
            type: "switch",
            op: null,
            value: "enabled",
            defaultValue: true,
            description: "Enables Sightengine content moderation for team names.",
          },
          {
            name: "mode",
            label: "Mode",
            type: "textInput",
            op: null,
            value: "mode",
            defaultValue: "rules,ml",
            description: "Sightengine moderation mode, e.g. 'rules,ml' to combine rule-based and ML checks.",
          },
          {
            name: "apiUser",
            label: "API User",
            type: "textInput",
            op: null,
            value: "apiUser",
            description: "Sightengine API user ID.",
          },
          {
            name: "apiSecret",
            label: "API Secret",
            type: "password",
            op: null,
            value: "apiSecret",
            description: "Sightengine API secret. Kept masked - click the eye icon to reveal.",
          },
          {
            name: "url",
            label: "URL",
            type: "textInput",
            op: null,
            value: "url",
            defaultValue: "https://api.sightengine.com/1.0/text/check.json",
            description: "Sightengine API endpoint URL.",
            fullWidth: true,
          },
          {
            name: "langs",
            label: "Langs",
            type: "textInput",
            op: null,
            value: "langs",
            defaultValue: "en,es,fr,pt,tr",
            description: "Comma-separated language codes Sightengine should check against, e.g. 'en,es,fr'.",
          },
          {
            name: "categories",
            label: "Categories",
            type: "textInput",
            op: null,
            value: "categories",
            defaultValue:
              "profanity,personal,link,drug,weapon,spam,content-trade,money-transaction,extremism,violence,self-harm,medical",
            description: "Comma-separated Sightengine moderation categories to check, e.g. 'profanity,personal,link'.",
          },
          {
            name: "mlThreshold",
            label: "ML Threshold",
            type: "textInput",
            op: null,
            value: "mlThreshold",
            dataType: "Number",
            defaultValue: 0.05,
            description: "ML confidence threshold (0-1) above which a match blocks the name.",
          },
          {
            name: "timeoutMs",
            label: "Timeout Ms",
            type: "textInput",
            op: null,
            value: "timeoutMs",
            dataType: "Number",
            defaultValue: 5000,
            description: "Sightengine request timeout, in milliseconds.",
          },
          {
            name: "textListEnabled",
            label: "Text List Enabled",
            type: "switch",
            op: null,
            value: "textListEnabled",
            defaultValue: false,
            description:
              "When on, Sightengine also checks team names against a custom text list (blacklist/whitelist) from the dashboard.",
          },
          {
            name: "textListId",
            label: "Text List ID",
            type: "textInput",
            op: null,
            value: "textListId",
            defaultValue: "",
            description:
              "Sightengine custom text list id (starts with tli_). Used as the 'list' param when Text List is enabled.",
          },
        ],
      },
    ],
  },
];

const DefaultValueBasedOnType = {
  switch: false,
  textInput: null,
  multiSelect: [],
  select: null,
  object: {}, // Default value for object type
};

// Fills in each object-type field's defaultValue when the loaded/initial
// data is missing that key or holds null - otherwise a field the admin
// never touched (or that a prior buggy save nulled out) stays permanently
// null/absent forever, since onSave only serializes keys already present
// in this data object and the backend does a full-object replace, not a
// per-field merge. Recurses one level into "group" fields.
const seedObjectDefaults = (configListDet: any, data: any) => {
  if (configListDet?.type !== "object" || !configListDet.objectValues) {
    return data;
  }

  const seeded = { ...(data ?? {}) };

  for (const field of configListDet.objectValues) {
    if (field.type === "group") {
      seeded[field.name] = seedObjectDefaults(field, seeded[field.name]);
    } else if ((seeded[field.name] === undefined || seeded[field.name] === null) && field.defaultValue !== undefined) {
      seeded[field.name] = field.defaultValue;
    }
  }

  return seeded;
};

const SaveButton = ({
  value,
  objectType = false,
  onSave = async (val?: any) => {},
}) => {
  const classes = styles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const _onSave = async () => {
    try {
      setLoading(true);
      await onSave(value);
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setLoading(false);
    }
  };
  return (
    <IconButton
      disableRipple
      onClick={_onSave}
      className={classes.saveIconButton}
      title="Save Configuration"
    >
      {loading ? (
        <CircularProgress size={20} sx={{ color: "white" }} />
      ) : (
        <Save fontSize="small" />
      )}
    </IconButton>
  );
};

const Configurations = () => {
  const classes = styles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const [wcModeEnabled, setWcModeEnabled] = React.useState(false);
  const [wcModeLoading, setWcModeLoading] = React.useState(false);
  const [wcModeDialogOpen, setWcModeDialogOpen] = React.useState(false);
  const [configDetails, setConfigDetails] = React.useState(
    configurationsList.reduce((acc, config) => {
      const data = DefaultValueBasedOnType[config.type];
      acc[config.value] = { data: config.type === "object" ? seedObjectDefaults(config, data) : data };
      return acc;
    }, {}),
  );
  const [options, setOptions] = React.useState({
    countryOptions: [],
    unitOptions: [
      { id: "minutes", label: "Minutes" },
      { id: "seconds", label: "Seconds" },
    ],
  });
  const [visiblePasswords, setVisiblePasswords] = React.useState<Record<string, boolean>>({});

  const FieldInfoTooltip = ({ text }: { text?: string }) => {
    if (!text) return null;
    return (
      <Tooltip title={text} arrow placement="top">
        <InfoOutlined
          fontSize="small"
          style={{ color: "#9e9e9e", cursor: "help", verticalAlign: "middle" }}
        />
      </Tooltip>
    );
  };

  const renderObjectField = (
    item: any,
    configValue: string,
    groupName?: string,
    paired?: boolean,
  ) => {
    const name = groupName
      ? `${configValue}.${groupName}.${item.name}`
      : `${configValue}.${item.name}`;
    const val = groupName
      ? configDetails[configValue]?.data?.[groupName]?.[item.name]
      : configDetails[configValue]?.data?.[item.name];
    const commonProps = {
      label: item.label,
      name,
      onChange,
      button: <FieldInfoTooltip text={item.description} />,
    };
    const defaultPlaceholder =
      item.defaultValue !== undefined ? String(item.defaultValue) : undefined;
    const wrapperClassName =
      paired && item.fullWidth
        ? `${classes.fieldContainer} ${classes.fieldFullWidth}`
        : classes.fieldContainer;

    switch (item.type) {
      case "multiSelect":
        return (
          <Box key={item.value} className={wrapperClassName}>
            <SimpleMultiSelectInput
              {...commonProps}
              value={val ?? []}
              options={options[item.op]}
            />
          </Box>
        );
      case "select":
        return (
          <Box key={item.value} className={wrapperClassName}>
            <SimpleSelectInput
              {...commonProps}
              value={val ?? ""}
              options={options[item.op]}
            />
          </Box>
        );
      case "textInput": {
        const isConsecutiveLimitField = item.name === "consecutiveNumbersLimit";
        const consecutiveLimitValue =
          isConsecutiveLimitField && (val === "" || val == null || Number(val) < 1) ? 1 : val;

        return (
          <Box key={item.value} className={wrapperClassName}>
            <SimpleTextInput
              {...commonProps}
              value={isConsecutiveLimitField ? consecutiveLimitValue : (val ?? "")}
              placeholdertext={defaultPlaceholder}
              {...(isConsecutiveLimitField ? { type: "number", inputProps: { min: 1, step: 1 } } : {})}
            />
          </Box>
        );
      }
      case "password": {
        const isVisible = !!visiblePasswords[name];
        return (
          <Box key={item.value} className={wrapperClassName}>
            <SimpleTextInput
              {...commonProps}
              value={val ?? ""}
              type={isVisible ? "text" : "password"}
              autoComplete="new-password"
              placeholdertext={defaultPlaceholder}
              adornment={
                <IconButton
                  size="small"
                  edge="end"
                  onClick={() =>
                    setVisiblePasswords((prev) => ({ ...prev, [name]: !prev[name] }))
                  }
                >
                  {isVisible ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              }
            />
          </Box>
        );
      }
      case "switch": {
        const effectiveVal = val ?? item.defaultValue ?? false;
        return (
          <Box key={item.value} className={wrapperClassName}>
            <Box display="flex" alignItems="center" gap={1}>
              <SimpleSwitch
                {...commonProps}
                legendText={item.label}
                value={!!effectiveVal}
                label={effectiveVal ? "Enabled" : "Disabled"}
              />
              <FieldInfoTooltip text={item.description} />
            </Box>
          </Box>
        );
      }
      case "group":
        if (groupName) {
          // Only one level of group nesting is supported by onChange/onSave.
          // A group rendered inside another group would silently write to the
          // wrong data path, so refuse to render it instead of doing that.
          console.error(
            `Configurations: nested "group" fields inside another group are not supported (attempted: ${groupName}.${item.name})`,
          );
          return null;
        }
        return (
          <Box
            key={item.value}
            className={paired ? `${classes.fieldContainer} ${classes.fieldFullWidth}` : classes.fieldContainer}
          >
            <Box
              sx={{
                borderTop: "1px dashed #ccc",
                marginTop: "8px",
                paddingTop: "12px",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#1D1030", marginBottom: "8px" }}
              >
                {item.label}
              </Typography>
            </Box>
            <Box className={paired ? classes.pairedFieldsContainer : classes.objectFieldsContainer}>
              {item.objectValues?.map((subItem: any) =>
                renderObjectField(subItem, configValue, item.name, paired),
              )}
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  const coerceFieldValue = (fieldConfig: any, value: any) => {
    if (fieldConfig?.dataType === "Number") {
      // Accepts integers and decimals (e.g. "0.05"). A blank/invalid input
      // falls back to the field's own default instead of null - null would
      // silently mask the intended value forever, since save sends a full
      // object replace, not a per-field merge.
      return /^\d+(\.\d+)?$/.test(String(value)) ? Number(value) : (fieldConfig?.defaultValue ?? null);
    }
    if (fieldConfig?.dataType === "Boolean") {
      return value === "true" || value === true;
    }
    return value;
  };

  const onSave = async (type) => {
    try {
      const configListDet = configurationsList.find((l) => l.name === type);
      let data = configDetails[type]?.data ?? {};

      // Handle number and boolean fields for object type
      if (configListDet?.type === "object") {
        data = Object.entries(data).reduce((acc, [key, value]) => {
          const fieldConfig: any = configListDet.objectValues.find(
            (field) => field.name === key,
          );

          if (fieldConfig?.type === "group") {
            acc[key] = Object.entries(value ?? {}).reduce(
              (groupAcc, [groupKey, groupValue]) => {
                const groupFieldConfig = fieldConfig.objectValues.find(
                  (field) => field.name === groupKey,
                );
                groupAcc[groupKey] = coerceFieldValue(groupFieldConfig, groupValue);
                return groupAcc;
              },
              {},
            );
          } else {
            acc[key] = coerceFieldValue(fieldConfig, value);
          }

          return acc;
        }, {});
      } else if (configListDet?.dataType === "Number") {
        data = /^\d+(\.\d+)?$/.test(data) ? Number(data) : data; // Check if value is numeric (integer or decimal)
      }

      if (type === "TEAM_NAME_CONFIG") {
        const limit = Number(data.consecutiveNumbersLimit);
        data.consecutiveNumbersLimit = !Number.isFinite(limit) || limit < 1 ? 1 : limit;

        if (data.sightengine?.textListEnabled && !String(data.sightengine?.textListId ?? "").trim()) {
          throw new Error("Text List ID is required when Text List is enabled.");
        }
      }

      const payload = { type, data };
      const response = await createAndUpdateConfig(payload);
      showSnackbar(response?.message);
    } catch (error) {
      throw error;
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;

    const [configName, ...pathParts] = name.split(".");
    const [fieldName, groupFieldName] = pathParts;
    const configListDet = configurationsList.find((l) => l.name === configName);

    setConfigDetails((prev) => {
      let updatedValue = value;

      if (groupFieldName) {
        // Nested group field, e.g. TEAM_NAME_CONFIG.sightengine.apiUser
        const groupConfig: any = configListDet?.objectValues?.find(
          (field) => field.name === fieldName && field.type === "group",
        );
        const fieldConfig = groupConfig?.objectValues?.find(
          (field) => field.name === groupFieldName,
        );

        // Number fields are NOT coerced here - see the flat-field branch below for why.
        if (fieldConfig?.dataType === "Boolean") {
          updatedValue = value === "true" || value === true || value === "on";
        } else if (fieldConfig?.type === "switch") {
          const currentEffective =
            prev[configName]?.data?.[fieldName]?.[groupFieldName] ?? fieldConfig?.defaultValue ?? false;
          updatedValue = !currentEffective;
        }

        updatedValue = {
          ...(prev[configName]?.data ?? {}),
          [fieldName]: {
            ...(prev[configName]?.data?.[fieldName] ?? {}),
            [groupFieldName]: updatedValue,
          },
        };
      } else if (fieldName) {
        const fieldConfig: any = configListDet?.objectValues?.find(
          (field) => field.name === fieldName,
        );

        // Handle boolean fields. Number fields are NOT coerced here - the raw
        // string is kept in state so decimals can be typed character-by-character;
        // coercion happens once, at save time, in coerceFieldValue.
        if (fieldConfig?.dataType === "Boolean") {
          updatedValue = value === "true" || value === true || value === "on"; // Handle switch toggles
        } else if (fieldConfig?.type === "switch") {
          const currentEffective =
            prev[configName]?.data?.[fieldName] ?? fieldConfig?.defaultValue ?? false;
          updatedValue = !currentEffective; // Toggle away from whatever is currently displayed (default-resolved)
        }

        updatedValue = {
          ...(prev[configName]?.data ?? {}),
          [fieldName]: updatedValue,
        };
      } else {
        if (configListDet?.type === "switch") {
          updatedValue = !prev[configName]?.data; // Toggle boolean for switch
        }
      }

      return {
        ...prev,
        [configName]: {
          ...prev[configName],
          data: updatedValue,
        },
      };
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      //get all configs
      const response = await getConfigurations();

      if (response?.data?.length) {
        const _modifiedData = response?.data?.reduce((acc, curr) => {
          const configListDet = configurationsList.find((l) => l.name === curr.type);
          const data =
            configListDet?.type === "object" ? seedObjectDefaults(configListDet, curr.data) : curr.data;
          acc[curr.type] = { ...curr, data };
          return acc;
        }, {});

        setConfigDetails(_modifiedData);

        const wcConfig = response.data.find(
          (c: any) => c.type === "WORLD_CUP_MODE",
        );
        setWcModeEnabled(wcConfig?.data?.isEnabled ?? false);
      }

      setOptions((prev) => {
        return {
          ...prev,
          countryOptions: sessionHelperInstance.getCountryOptions(),
        };
      });
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleWcModeToggleClick = () => setWcModeDialogOpen(true);

  const handleWcModeConfirm = async () => {
    setWcModeLoading(true);
    try {
      const newValue = !wcModeEnabled;
      const resp = await toggleWorldCupMode(newValue);
      setWcModeEnabled(resp?.data?.isEnabled ?? newValue);
      showSnackbar(
        `World Cup Mode ${newValue ? "enabled" : "disabled"} successfully`,
      );
      setWcModeDialogOpen(false);
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setWcModeLoading(false);
    }
  };

  const handleWcModeDialogClose = () => {
    if (!wcModeLoading) setWcModeDialogOpen(false);
  };

  React.useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ContentShell title="Configurations" loading={loading}>
      {/* World Cup Mode Section */}
      {sessionHelper.hasRole(PermittedRoles.WORLD_CUP_MODE.TOGGLE_ROLES) && (
        <>
          <Typography
            variant="h2"
            className={classes.sectionTitle}
            sx={{ mb: 3 }}
          >
            World Cup Mode
          </Typography>
          <Box mb={4}>
            <Paper
              elevation={0}
              sx={{
                background: wcModeEnabled
                  ? "linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)"
                  : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                border: wcModeEnabled
                  ? "1px solid #f5a623"
                  : "1px solid #e9ecef",
                borderRadius: "12px",
                padding: "24px",
                maxWidth: 600,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography
                    variant="h3"
                    sx={{ fontSize: "18px", fontWeight: 600, color: "#1D1030" }}
                  >
                    ⚽ World Cup Mode
                  </Typography>
                  <Chip
                    label={wcModeEnabled ? "ENABLED" : "DISABLED"}
                    size="small"
                    sx={{
                      backgroundColor: wcModeEnabled ? "#4caf50" : "#9e9e9e",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "11px",
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={handleWcModeToggleClick}
                  sx={{
                    backgroundColor: wcModeEnabled ? "#f44336" : "#f5a623",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: wcModeEnabled ? "#d32f2f" : "#e0941f",
                    },
                  }}
                >
                  {wcModeEnabled ? "Disable" : "Enable"}
                </Button>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 1.5 }}
              >
                {wcModeEnabled
                  ? "World Cup Mode is active. New players receive national team badges and kits based on their locale."
                  : "Enable to activate the FIFA World Cup 2026 visual theme. New players will receive national team badges and kits based on their locale."}
              </Typography>
            </Paper>
          </Box>

          <WorldCupModeConfirmDialog
            open={wcModeDialogOpen}
            onClose={handleWcModeDialogClose}
            onConfirm={handleWcModeConfirm}
            isCurrentlyEnabled={wcModeEnabled}
            loading={wcModeLoading}
          />
        </>
      )}

      {/* Simple Configuration Fields */}
      <Typography variant="h2" className={classes.sectionTitle}>
        Basic Configurations
      </Typography>
      <Box className={classes.configGrid}>
        {configurationsList
          .filter(
            (i) =>
              !i.objectValues &&
              (!i.requiredPermission ||
                sessionHelper.hasRole(i.requiredPermission)),
          )
          .map(({ type, op, value, ...rest }) => {
            const renderInputField = () => {
              const commonProps = {
                ...rest,
                value: configDetails[value]?.data,
                onChange,
              };

              switch (type) {
                case "multiSelect":
                  return (
                    <Box className={classes.inputFieldWrapper}>
                      <SimpleMultiSelectInput
                        {...commonProps}
                        value={configDetails[value]?.data ?? []}
                        options={options[op]}
                      />
                    </Box>
                  );
                case "select":
                  return (
                    <Box className={classes.inputFieldWrapper}>
                      <SimpleSelectInput
                        {...commonProps}
                        value={configDetails[value]?.data ?? ""}
                        options={options[op]}
                      />
                    </Box>
                  );
                case "textInput":
                  return (
                    <Box className={classes.inputFieldWrapper}>
                      <SimpleTextInput
                        {...commonProps}
                        value={configDetails[value]?.data ?? ""}
                      />
                    </Box>
                  );
                case "switch":
                  return (
                    <Box className={classes.inputFieldWrapper}>
                      <SimpleSwitch
                        legendText={rest.label}
                        name={rest.name}
                        value={!!configDetails[value]?.data}
                        label={
                          configDetails[value]?.data ? "Enabled" : "Disabled"
                        }
                        onChange={onChange}
                      />
                    </Box>
                  );
                default:
                  return null;
              }
            };

            return (
              <Paper
                key={value}
                elevation={0}
                className={classes.configurationCard}
              >
                <Box className={classes.simpleConfigContainer}>
                  {renderInputField()}
                  <SaveButton onSave={onSave} value={value} />
                </Box>
              </Paper>
            );
          })}
      </Box>

      {/* Object Configuration Fields */}
      <Typography variant="h2" className={classes.sectionTitle}>
        Advanced Configurations
      </Typography>
      <Box className={classes.objectConfigGrid}>
        {configurationsList
          .filter(
            (i) =>
              i.objectValues &&
              (!i.requiredPermission ||
                sessionHelper.hasRole(i.requiredPermission)),
          )
          .map(({ value, ...rest }) => (
            <Paper
              key={value}
              elevation={0}
              className={rest.paired ? classes.objectConfigCardWide : classes.objectConfigCard}
            >
              <Box className={classes.configHeader}>
                <Typography variant="h3" className={classes.configTitle}>
                  {rest.label}
                </Typography>
                <SaveButton onSave={onSave} value={value} objectType />
              </Box>

              <Box className={rest.paired ? classes.pairedFieldsContainer : classes.objectFieldsContainer}>
                {rest.objectValues?.map((item) =>
                  renderObjectField(item, value, undefined, rest.paired),
                )}
              </Box>
            </Paper>
          ))}
      </Box>
    </ContentShell>
  );
};

export default Configurations;
